import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import FontAwesome from 'react-fontawesome';
import { toast } from 'react-toastify';
import { axiosCaller } from '../../../utils/apiCaller';
import KubitBomBuilder from '../../Admin/ArticlesNew/components/KubitBomBuilder';
import { KubitBomRow, KubitRawMaterialOption } from '../../Admin/ArticlesNew/ArticleForm';
// KubitBomBuilder's styles (.art-builder / .art-bom / .art-kbom / .art-form-field*).
// articles.scss is fully namespaced (all rules target .art-* classes), so importing
// it here only styles the builder and cannot leak into the offer modal.
import '../../Admin/ArticlesNew/articles.scss';

interface Props {
    productId: number;
    readOnly?: boolean;
}

const toChip = (list: any[]) =>
    (list || []).map((c) => ({ value: c.value ?? c.id, label: c.label ?? c.name }));

const normalizeOptions = (list: any[]): KubitRawMaterialOption[] =>
    (list || []).map((r) => ({
        value: r.value ?? r.id,
        label: r.label ?? r.name ?? String(r.value ?? r.id),
        unitId: r.unitId ?? r.unit?.id ?? null,
        unitLabel: r.unit?.name ?? r.unit?.label ?? undefined,
        consumeResponsibilities: toChip(r.consumeResponsibilities),
    }));

const normalizeRows = (list: any[]): KubitBomRow[] =>
    (list || []).map((r) => ({
        value: r.value ?? r.id,
        label: r.label ?? r.name ?? String(r.value ?? r.id),
        quantity: r.quantity != null ? Number(r.quantity) : 1,
        unitId: r.unitId ?? r.unit?.id ?? null,
        unitLabel: r.unit?.name ?? r.unit?.label ?? undefined,
    }));

/**
 * KuBIT-only per-offer bill of materials for a manufactured (type-4) product line.
 * Shows the article's template BOM (seeded server-side on first open), lets the
 * user edit quantities and add/remove raw materials, and saves the whole set with a
 * single PUT. Reuses the article-form KubitBomBuilder (two-panel picker + quantity
 * table + read-only unit); the unit stays the raw material's own.
 */
const OfferBomSection: React.FC<Props> = ({ productId, readOnly }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    const [rows, setRows] = useState<KubitBomRow[]>([]);
    const [options, setOptions] = useState<KubitRawMaterialOption[]>([]);
    const [collapsed, setCollapsed] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [dirty, setDirty] = useState<boolean>(false);

    // Raw-material picker options (the company's type-3 articles).
    useEffect(() => {
        axiosCaller('GET', 'articles/kubit-raw-materials')
            .then((res: any) => setOptions(normalizeOptions(res?.data?.data ?? res?.data)))
            .catch(() => undefined);
    }, []);

    // This line's per-offer BOM (seeds from the template on the server's first read).
    useEffect(() => {
        if (!productId) return;
        setLoading(true);
        axiosCaller('GET', `products/${productId}/kubit-bom`)
            .then((res: any) => { setRows(normalizeRows(res?.data?.data ?? res?.data)); setDirty(false); })
            .catch(() => undefined)
            .finally(() => setLoading(false));
    }, [productId]);

    const onChange = (next: KubitBomRow[]) => { setRows(next); setDirty(true); };

    const save = async () => {
        setSaving(true);
        try {
            const res: any = await axiosCaller('PUT', `products/${productId}/kubit-bom`, {
                components: rows.map((r) => ({
                    componentArticleId: r.value,
                    quantity: r.quantity === '' ? 1 : r.quantity,
                })),
            });
            setRows(normalizeRows(res?.data?.data ?? res?.data));
            setDirty(false);
            toast.success(fm('offer.kubitBom.toast.saved', 'BOM u ruajt'));
        } catch (e) {
            toast.error(fm('offer.kubitBom.toast.saveFailed', 'Ruajtja e BOM dështoi'));
        } finally {
            setSaving(false);
        }
    };

    if (!productId) return null;

    return (
        <div className="sp-section">
            <div className="sp-section-header" onClick={() => setCollapsed((c) => !c)}>
                <div className="sp-section-header-left">
                    <FontAwesome name="chevron-down" className={`sp-section-chevron ${collapsed ? 'is-collapsed' : ''}`} />
                    <span className="sp-section-title">
                        <FormattedMessage id="offer.kubitBom.title" defaultMessage="Lëndët e para (KuBIT)" />
                        <span className="sp-section-count"> ({rows.length})</span>
                    </span>
                </div>
                {!collapsed && !readOnly && (
                    <button
                        type="button"
                        className="sp-add-btn"
                        disabled={saving || !dirty}
                        onClick={(e) => { e.stopPropagation(); save(); }}
                    >
                        {saving ? fm('common.saving', 'Duke ruajtur...') : fm('offer.kubitBom.save', 'Ruaj BOM')}
                    </button>
                )}
            </div>

            {!collapsed && (
                <div style={{ padding: '10px 12px' }}>
                    {loading ? (
                        <div className="art-builder__lib-empty">
                            <FormattedMessage id="common.loading" defaultMessage="Duke ngarkuar..." />
                        </div>
                    ) : (
                        <KubitBomBuilder
                            options={options}
                            value={rows}
                            units={[]}
                            onChange={readOnly ? () => undefined : onChange}
                        />
                    )}
                    <p className="art-form-field__hint" style={{ marginTop: '10px' }}>
                        <FormattedMessage
                            id="offer.kubitBom.hint"
                            defaultMessage="Sasitë fillojnë nga shablloni i artikullit; ndryshimet ruhen vetëm për këtë ofertë."
                        />
                    </p>
                </div>
            )}
        </div>
    );
};

export default OfferBomSection;
